/**
 * Seeded pseudo-random numbers (GEN-001, and the fuzz harness).
 *
 * `Math.random` is never used anywhere in this project (PRD N2/§8): a level has to be
 * reproducible from its seed, and a fuzz failure has to be reproducible from its seed
 * or it is not a bug report.
 *
 * Lives in `sim` for the same reason the physics does — both `levelgen` (bulk
 * generation offline) and `apps/web` (the layout lab, and any later client-side check)
 * must get **identical** layouts from the same seed, so the mapping cannot live in a
 * package only one of them can import.
 *
 * Only exact integer operations are used — `^`, `|`, `<<`, `>>>` and `Math.imul`, all
 * defined exactly by the spec — so this can no more diverge between engines than the
 * physics can.
 */

/** A stream of numbers in [0, 1). Deterministic for a given seed. */
export interface Rng {
  /** Next value in [0, 1). */
  next(): number;
  /** Next value in [min, max). */
  range(min: number, max: number): number;
  /** Next integer in [min, max]. */
  int(min: number, max: number): number;
}

/**
 * cyrb128: a string → four 32-bit words, for seeding. A single 32-bit seed gives
 * sfc32 too little state and visibly correlated streams for similar seeds — and our
 * seeds are things like "2026-09-21", which differ by one character.
 */
function cyrb128(seed: string): [number, number, number, number] {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;

  for (let i = 0; i < seed.length; i++) {
    const k = seed.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }

  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);

  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

/** sfc32 — small, fast, and good enough for level layouts and fuzzing. */
export function createRng(seed: string): Rng {
  const [s0, s1, s2, s3] = cyrb128(seed);
  let a = s0;
  let b = s1;
  let c = s2;
  let d = s3;

  // Discard the first few outputs: sfc32 needs a moment to mix a fresh seed.
  const next = (): number => {
    a |= 0;
    b |= 0;
    c |= 0;
    d |= 0;
    const t = (((a + b) | 0) + d) | 0;
    d = (d + 1) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };

  for (let i = 0; i < 12; i++) next();

  return {
    next,
    range: (min, max) => min + next() * (max - min),
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
  };
}
